import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Feedback {
  id: string
  source: string
  category: string
  sentiment: string
  text: string
  date: string
  score: number
}

interface Theme {
  id: string
  name: string
  mentions: number
  sentiment_score: number
  trend: string
  keywords: string[]
}

interface Channel {
  id: string
  name: string
  responses: number
  avg_sentiment: number
  response_rate: number
}

interface Statistics {
  total_feedback: number
  avg_sentiment: number
  response_rate: number
  top_theme: string
}

export default function VoiceOfCustomerPage() {
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [themes, setThemes] = useState<Theme[]>([])
  const [channels, setChannels] = useState<Channel[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [feedbackRes, themesRes, channelsRes, statsRes] = await Promise.all([
        fetch('/api/voiceofcustomer/feedback'),
        fetch('/api/voiceofcustomer/themes'),
        fetch('/api/voiceofcustomer/channels'),
        fetch('/api/voiceofcustomer/statistics')
      ])
      if (feedbackRes.ok) setFeedback(await feedbackRes.json())
      if (themesRes.ok) setThemes(await themesRes.json())
      if (channelsRes.ok) setChannels(await channelsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching VoC data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSentimentBadge = (sentiment: string) => {
    if (sentiment === 'positive') return 'bg-green-100 text-green-800'
    if (sentiment === 'negative') return 'bg-red-100 text-red-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return '↑'
    if (trend === 'down') return '↓'
    return '→'
  }

  const getTrendColor = (trend: string) => {
    if (trend === 'up') return 'text-green-600'
    if (trend === 'down') return 'text-red-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Voice of Customer</h1>
          <p className="mt-2 text-gray-600">Phase 222 - Customer feedback and sentiment analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Feedback</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_feedback.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Sentiment</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_sentiment}/10</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Response Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.response_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Theme</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.top_theme}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Emerging Themes</h2>
            </div>
            <div className="p-6 space-y-4">
              {themes.map((theme) => (
                <div key={theme.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{theme.name}</span>
                    <span className={`text-sm ${getTrendColor(theme.trend)}`}>
                      {getTrendIcon(theme.trend)} {theme.mentions} mentions
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${theme.sentiment_score >= 7 ? 'bg-green-500' : theme.sentiment_score >= 4 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${theme.sentiment_score * 10}%` }}
                      />
                    </div>
                    <span className="text-gray-500 text-sm">{theme.sentiment_score}/10</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {theme.keywords.map((kw, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{kw}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Feedback Channels</h2>
            </div>
            <div className="p-6 space-y-4">
              {channels.map((channel) => (
                <div key={channel.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{channel.name}</p>
                    <p className="text-sm text-gray-500">{channel.responses.toLocaleString()} responses</p>
                  </div>
                  <div className="text-right">
                    <p className={channel.avg_sentiment >= 7 ? 'text-green-600' : channel.avg_sentiment >= 4 ? 'text-yellow-600' : 'text-red-600'}>
                      {channel.avg_sentiment}/10 sentiment
                    </p>
                    <p className="text-sm text-blue-600">{channel.response_rate}% response rate</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Feedback</h2>
          </div>
          <div className="p-6 space-y-4">
            {feedback.map((fb) => (
              <div key={fb.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">{fb.source}</span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">{fb.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-sm ${getSentimentBadge(fb.sentiment)}`}>
                      {fb.sentiment}
                    </span>
                    <span className="text-gray-500 text-sm">{fb.date}</span>
                  </div>
                </div>
                <p className="text-gray-700">{fb.text}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
